import React from "react";
import { CoreButton, CoreIconButton, CoreProgressBar } from "@verity/components/core";
import CancelSvg from "../../../../images/settings/cross-x.svg?react";
import { Link, styled } from "@mui/material";
import { CustomSettingData } from "../../settings-page";
import i18n from "../../../../i18n";
import apiClient from "../../../../helpers/api-client";
import { triggerFileDownload } from "../../../../helpers/download";

export const AgreementFileInput = (props: {
	loadData: { fileName?: string | null } | null;
	setting: CustomSettingData<File>;
	onSettingChange: (value?: File, options?: { delete?: boolean }) => void;
	instituteId?: number;
	disabled?: boolean;
}) => {
	const [fileDrag, setFileDrag] = React.useState(false);
	const [invalidFile, setInvalidFile] = React.useState(false);

	const [selectedFile, setSelectedFile] = React.useState<File>();
	const [deleteFile, setDeleteFile] = React.useState(false);

	const [presignedUrl, setPresignedUrl] = React.useState<string | undefined>(undefined);

	React.useEffect(() => {
		setDeleteFile(props.setting?.options?.delete ? true : false);
		setSelectedFile(props.setting?.value);
	}, [props.setting]);

	React.useEffect(() => {
		const fetchUrl = async () => {
			if (props.instituteId && props.loadData?.fileName && !selectedFile) {
				try {
					const url = await apiClient.institute.getPolicyPdfFromInstituteId(props.instituteId);
					setPresignedUrl(url);
				} catch (error) {
					console.error("Failed to fetch presigned URL for agreement file:", error);
				}
			}
		};

		fetchUrl();
	}, [props.instituteId, props.loadData?.fileName, selectedFile]);

	const Cancel = styled(CancelSvg)({
		width: "18px",
		minWidth: "18px",
		height: "21px",
		cursor: props.disabled ? "default" : "pointer",
		"& path": { stroke: props.disabled ? "#ddd" : "#525252" },
	});

	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const setDrag = (event: React.DragEvent<HTMLDivElement>, drag: boolean) => {
		if (props.disabled) return;
		event.stopPropagation();
		event.preventDefault();
		setFileDrag(drag);
	};

	return (
		<div
			onDragEnter={(e) => setDrag(e, true)}
			onDragOver={(e) => setDrag(e, true)}
			onDragLeave={(e) => setDrag(e, false)}
			onDrop={(e) => {
				if (props.disabled) return;
				setDrag(e, false);
				const files = e.dataTransfer.files;
				if (files?.[0] && files[0].type === "application/pdf") {
					setSelectedFile(files[0]);
					props.onSettingChange(files[0], { delete: deleteFile });
					setInvalidFile(false);
				} else {
					setInvalidFile(true);
				}
			}}
			style={{
				height: fileDrag ? ((!deleteFile && props.loadData?.fileName) || selectedFile ? "59px" : "29px") : undefined,
				backgroundColor: fileDrag ? "#e7f1f3" : undefined,
				border: fileDrag ? "#0b6f85 2px dashed" : undefined,
				display: fileDrag ? "flex" : undefined,
				color: fileDrag ? "#0b6f85" : undefined,
				borderRadius: "8px",
				placeContent: "center",
				alignItems: "center",
				fontSize: "16px",
			}}
		>
			{fileDrag ? (
				i18n.t("portal.components.settings.instituteAgreementInput.indexTSX.pdf")
			) : (
				<>
					<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
						<CoreButton
							style={{
								display: "inline-block",
								fontSize: "13px",
								marginRight: "8px",
								whiteSpace: "nowrap",
							}}
							onClick={() => fileInputRef.current?.click()}
							disabled={props.disabled}
						>
							{i18n.t("portal.components.settings.instituteAgreementInput.indexTSX.upload")}
						</CoreButton>
						<input
							type="file"
							id="agreement-file"
							accept={"application/pdf"}
							onChange={(event) => {
								if (event.target.files?.[0] && event.target.files?.[0].type === "application/pdf") {
									setSelectedFile(event.target.files[0]);
									props.onSettingChange(event.target.files[0], { delete: deleteFile });
									setInvalidFile(false);
								} else {
									setInvalidFile(true);
								}
							}}
							ref={fileInputRef}
							hidden
						/>
						{invalidFile ? (
							<div style={{ color: "red" }}>
								{i18n.t("portal.components.settings.instituteAgreementInput.indexTSX.invalid")}
							</div>
						) : null}
					</div>
					{((!deleteFile && props.loadData?.fileName) || selectedFile) && (
						<div style={{ fontSize: "15px", color: "#525252", paddingTop: "8px" }}>
							{selectedFile ? (
								<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
									{props.disabled && <CoreProgressBar width={"max(25%, 125px)"} />}
									<Link
										tabIndex={0}
										sx={{
											textDecoration: props.disabled ? "none" : "underline",
											cursor: props.disabled ? undefined : "pointer",
											"&:focus-visible": {
												outline: "2px solid var(--focus-outline-color-dark)",
												outlineOffset: "2px",
												borderRadius: "2px",
											},
										}}
										onClick={() => {
											if (props.disabled) return;
											triggerFileDownload(selectedFile, selectedFile.name);
										}}
									>
										{selectedFile.name}
									</Link>
									<CoreIconButton
										style={{ padding: 0, margin: 0, border: 0, background: "none", height: "21px" }}
										onClick={() => {
											setSelectedFile(undefined);
											setDeleteFile(true);
											props.onSettingChange(undefined, { delete: true });
											if (fileInputRef.current) {
												fileInputRef.current.value = "";
											}
										}}
										disabled={props.disabled}
										variant="plain"
									>
										<Cancel />
									</CoreIconButton>
								</div>
							) : (
								<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
									{!props.disabled && presignedUrl? (
										<Link
											href={presignedUrl}
											rel="noreferrer"
											target="_blank"
											sx={{
												"&:focus-visible": {
													outline: "2px solid var(--focus-outline-color-dark)",
													outlineOffset: "2px",
													borderRadius: "2px",
												},
											}}
										>
											{props.loadData?.fileName}
										</Link>
									) : (
										<span>{props.loadData?.fileName}</span>
									)}
									<CoreIconButton
										style={{ padding: 0, margin: 0, border: 0, background: "none", height: "21px" }}
										onClick={() => {
											setDeleteFile(true);
											props.onSettingChange(selectedFile, { delete: true });
										}}
										disabled={props.disabled}
										variant="plain"
									>
										<Cancel />
									</CoreIconButton>
								</div>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export async function saveAgreementFile(
	file: File | null | undefined,
	instituteId?: number,
	options?: { delete?: boolean },
) {
	function getFileSize(bytes: number) {
		if (bytes < 1048576) {
			return `${(bytes / 1024).toFixed(1)} KB`;
		} else {
			return `${(bytes / 1048576).toFixed(1)} MB`;
		}
	}

	try {
		if (instituteId == null) {
			return { ok: false };
		}
		let resp: Response | null = null;
		if (file) {
			const ext = file.type.split("/")[1];
			// Only allow PDF for now
			if (ext !== "pdf") {
				throw new Error(i18n.t("portal.components.settings.instituteAgreementInput.indexTSX.wrong"));
			}
			const fileName = file.name.replace(/\.[^/.]+$/, "");
			resp = await apiClient.institute.uploadInstituteAgreement({
				instituteId,
				fileName: fileName,
				extension: ext,
				file: file,
				deleteFile: options?.delete,
			});
		} else if (options?.delete) {
			resp = await apiClient.institute.deleteInstituteAgreement({ instituteId });
		}
		if (resp && !resp.ok) {
			let errorReason: string | undefined;
			if (resp.status === 413) {
				errorReason = `${i18n.t("portal.components.settings.instituteAgreementInput.indexTSX.tooLarge")}
					${file?.size ? ` (${getFileSize(file.size)})` : ""}${i18n.t(
						"portal.components.settings.instituteAgreementInput.indexTSX.smaller",
					)}`;
			}
			return { ok: false, reason: errorReason };
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
	}
	return { ok: false };
}
